import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  FiMinus,
  FiPlus,
  FiRefreshCw,
  FiTarget,
} from "react-icons/fi";
import type { Point2D } from "../../../utils/smileDesign/dentalAccuracy/types";
import type {
  ToothSizeHandle,
  WireTeethModel,
  WireTooth,
} from "../../../utils/smileDesign/dentalAccuracy";
import {
  addWireToothHandle,
  moveWireTooth,
  moveWireToothHandle,
  removeWireToothHandle,
  resizeWireToothByHandle,
  paintEditedAfter,
  wireToothContour,
  wireToothDisplayBounds,
  wireToothHandlePoints,
} from "../../../utils/smileDesign/dentalAccuracy";

const SELECTED = "#2fa9bf";
const SIZE_FILL = "#e8c547";
const MIN_ZOOM = 1;
const MAX_ZOOM = 12;
const VIEW_MARGIN = 12;

interface Props {
  afterSrc: string;
  model: WireTeethModel;
  selectedId: string | null;
  /** Every tooth in the current selection. Ctrl/Cmd-click toggles membership. */
  selectedIds?: string[];
  selectedHandle: number | null;
  lockAspect: boolean;
  onSelect: (
    toothId: string | null,
    handleIndex: number | null,
    mode?: "replace" | "toggle" | "focus"
  ) => void;
  onToothChange: (tooth: WireTooth) => void;
}

type Drag =
  | { kind: "shape"; toothId: string; index: number }
  | { kind: "size"; toothId: string; handle: ToothSizeHandle }
  | { kind: "move"; toothId: string; lastX: number; lastY: number };

const SIZE_HANDLES: { id: ToothSizeHandle; cursor: string }[] = [
  { id: "nw", cursor: "nwse-resize" },
  { id: "n", cursor: "ns-resize" },
  { id: "ne", cursor: "nesw-resize" },
  { id: "e", cursor: "ew-resize" },
  { id: "se", cursor: "nwse-resize" },
  { id: "s", cursor: "ns-resize" },
  { id: "sw", cursor: "nesw-resize" },
  { id: "w", cursor: "ew-resize" },
];

function sizePoint(box: ReturnType<typeof wireToothDisplayBounds>, id: ToothSizeHandle): Point2D {
  const mx = (box.x0 + box.x1) / 2;
  const my = (box.y0 + box.y1) / 2;
  switch (id) {
    case "nw":
      return { x: box.x0, y: box.y0 };
    case "n":
      return { x: mx, y: box.y0 };
    case "ne":
      return { x: box.x1, y: box.y0 };
    case "e":
      return { x: box.x1, y: my };
    case "se":
      return { x: box.x1, y: box.y1 };
    case "s":
      return { x: mx, y: box.y1 };
    case "sw":
      return { x: box.x0, y: box.y1 };
    case "w":
      return { x: box.x0, y: my };
  }
}

function pathD(pts: Point2D[]): string {
  if (pts.length < 3) return "";
  return (
    pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ") +
    " Z"
  );
}

/**
 * After-photo canvas: outlines sit in the photo's own pixels. Click a tooth,
 * drag its points to reshape, drag the box to change size, drag the fill to move.
 */
export default function AfterToothCanvas({
  afterSrc,
  model,
  selectedId,
  selectedIds,
  selectedHandle,
  lockAspect,
  onSelect,
  onToothChange,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const photoCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const photoRef = useRef<HTMLImageElement | null>(null);
  const coverPixelsRef = useRef<Uint8ClampedArray | null>(null);
  const [baseScale, setBaseScale] = useState(1);
  const [viewport, setViewport] = useState({ w: 1, h: 1 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [spaceDown, setSpaceDown] = useState(false);
  const fittedRef = useRef(false);
  const panRef = useRef(pan);
  panRef.current = pan;
  const dragRef = useRef<Drag | null>(null);
  const panDragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const modelRef = useRef(model);
  modelRef.current = model;
  const lockRef = useRef(lockAspect);
  lockRef.current = lockAspect;
  const onToothChangeRef = useRef(onToothChange);
  onToothChangeRef.current = onToothChange;

  const width = model.width;
  const height = model.height;
  const scale = baseScale * zoom;
  const imageCx = width / 2;
  const imageCy = height / 2;

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el || !width || !height) return;
    const rect = el.getBoundingClientRect();
    const availW = Math.max(1, rect.width - VIEW_MARGIN * 2);
    const availH = Math.max(1, rect.height - VIEW_MARGIN * 2);
    setBaseScale(Math.min(availW / width, availH / height));
    setViewport({ w: rect.width, h: rect.height });
    return { availW, availH, width: rect.width, height: rect.height };
  }, [width, height]);

  const smileFocus = useMemo(() => {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const tooth of model.teeth) {
      const b = wireToothDisplayBounds(tooth);
      x0 = Math.min(x0, b.x0);
      y0 = Math.min(y0, b.y0);
      x1 = Math.max(x1, b.x1);
      y1 = Math.max(y1, b.y1);
    }
    if (!Number.isFinite(x0)) return { x: 0, y: 0, width, height };
    const padX = (x1 - x0) * 0.12;
    const padY = (y1 - y0) * 0.4;
    return {
      x: Math.max(0, x0 - padX),
      y: Math.max(0, y0 - padY),
      width: Math.min(width, x1 + padX) - Math.max(0, x0 - padX),
      height: Math.min(height, y1 + padY) - Math.max(0, y0 - padY),
    };
  }, [model.teeth, width, height]);

  const applyFullImageView = useCallback(
    (resetPan = true) => {
      measure();
      setZoom(1);
      if (resetPan) setPan({ x: 0, y: 0 });
      fittedRef.current = true;
    },
    [measure]
  );

  const applySmileZoom = useCallback(
    (resetPan = true) => {
      const m = measure();
      if (!m) return;
      const nextBase = Math.min(m.availW / width, m.availH / height);
      const z = Math.max(
        MIN_ZOOM,
        Math.min(
          MAX_ZOOM,
          Math.min(m.availW / Math.max(1, smileFocus.width * nextBase), m.availH / Math.max(1, smileFocus.height * nextBase))
        )
      );
      setZoom(z);
      if (resetPan) {
        const nextScale = nextBase * z;
        setPan({
          x: (imageCx - (smileFocus.x + smileFocus.width / 2)) * nextScale,
          y: (imageCy - (smileFocus.y + smileFocus.height / 2)) * nextScale,
        });
      }
      fittedRef.current = true;
    },
    [measure, width, height, smileFocus, imageCx, imageCy]
  );

  useEffect(() => {
    fittedRef.current = false;
  }, [afterSrc]);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (cancelled) return;
      photoRef.current = img;
      const probe = document.createElement("canvas");
      probe.width = width;
      probe.height = height;
      const pctx = probe.getContext("2d", { willReadFrequently: true });
      if (pctx) {
        pctx.drawImage(img, 0, 0, width, height);
        coverPixelsRef.current = pctx.getImageData(0, 0, width, height).data;
      }
      const canvas = photoCanvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (ctx) paintEditedAfter(ctx, img, modelRef.current, coverPixelsRef.current);
    };
    img.src = afterSrc;
    return () => {
      cancelled = true;
    };
  }, [afterSrc, width, height]);

  useEffect(() => {
    const canvas = photoCanvasRef.current;
    const img = photoRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !img || !ctx) return;
    paintEditedAfter(ctx, img, model, coverPixelsRef.current);
  }, [model]);

  useEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (fittedRef.current) return;
    if (viewport.w < 40 || baseScale <= 0) return;
    applySmileZoom(true);
  }, [viewport.w, baseScale, applySmileZoom]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        setSpaceDown(true);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpaceDown(false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const currentPan = panRef.current;
      setZoom((prevZoom) => {
        const delta = e.deltaY > 0 ? -0.15 : 0.15;
        const nextZoom = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, Math.round((prevZoom + delta) * 100) / 100)
        );
        if (nextZoom === prevZoom) return prevZoom;
        const prevScale = baseScale * prevZoom;
        const nextScale = baseScale * nextZoom;
        const imgX = (mx - (viewport.w / 2 - imageCx * prevScale + currentPan.x)) / prevScale;
        const imgY = (my - (viewport.h / 2 - imageCy * prevScale + currentPan.y)) / prevScale;
        setPan({
          x: mx - (viewport.w / 2 - imageCx * nextScale) - imgX * nextScale,
          y: my - (viewport.h / 2 - imageCy * nextScale) - imgY * nextScale,
        });
        return nextZoom;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [baseScale, viewport.w, viewport.h, imageCx, imageCy]);

  const offsetX = viewport.w / 2 - imageCx * scale + pan.x;
  const offsetY = viewport.h / 2 - imageCy * scale + pan.y;

  const clientToImage = useCallback(
    (clientX: number, clientY: number) => {
      const el = containerRef.current;
      if (!el) return { x: 0, y: 0 };
      const rect = el.getBoundingClientRect();
      return {
        x: (clientX - rect.left - offsetX) / scale,
        y: (clientY - rect.top - offsetY) / scale,
      };
    },
    [offsetX, offsetY, scale]
  );

  const uiScale = Math.min(2.4, Math.max(0.16, 1 / Math.max(0.001, scale)));
  const handlePx = 7.5 * uiScale;

  const shapes = useMemo(
    () =>
      model.teeth.map((tooth, index) => ({
        tooth,
        index,
        d: pathD(wireToothContour(tooth)),
      })),
    [model]
  );

  const selected = selectedId
    ? model.teeth.find((t) => t.id === selectedId) ?? null
    : null;
  const handlePts = selected ? wireToothHandlePoints(selected) : [];
  const box = selected ? wireToothDisplayBounds(selected) : null;

  const onBackgroundPointerDown = (e: ReactPointerEvent) => {
    const isPan = spaceDown || e.button === 1;
    if (!isPan) {
      if (e.button === 0) onSelect(null, null);
      return;
    }
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y,
    };
  };

  const groupIds = selectedIds?.length
    ? selectedIds
    : selectedId
      ? [selectedId]
      : [];

  const onToothPointerDown = (tooth: WireTooth) => (e: ReactPointerEvent) => {
    if (spaceDown || e.button !== 0) return;
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey) {
      onSelect(tooth.id, null, "toggle");
      return;
    }
    onSelect(tooth.id, null, groupIds.includes(tooth.id) ? "focus" : "replace");
    const pt = clientToImage(e.clientX, e.clientY);
    dragRef.current = { kind: "move", toothId: tooth.id, lastX: pt.x, lastY: pt.y };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onToothDoubleClick = (tooth: WireTooth) => (e: ReactMouseEvent) => {
    if (selectedId !== tooth.id) return;
    e.stopPropagation();
    const at = clientToImage(e.clientX, e.clientY);
    const next = addWireToothHandle(tooth, at);
    if (next === tooth) return;
    onToothChange(next);
    const pts = wireToothHandlePoints(next);
    let added = 0;
    let best = Infinity;
    pts.forEach((p, i) => {
      const d = Math.hypot(p.x - at.x, p.y - at.y);
      if (d < best) {
        best = d;
        added = i;
      }
    });
    onSelect(tooth.id, added, "focus");
  };

  const onShapeHandleDown = (tooth: WireTooth, index: number) => (e: ReactPointerEvent) => {
    if (spaceDown || e.button === 2) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: "shape", toothId: tooth.id, index };
    onSelect(tooth.id, index, "focus");
  };

  const onShapeHandleContext = (tooth: WireTooth, index: number) => (e: ReactMouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const next = removeWireToothHandle(tooth, index);
    if (next === tooth) return;
    onToothChange(next);
    onSelect(tooth.id, null, "focus");
  };

  const onSizeHandleDown = (tooth: WireTooth, handle: ToothSizeHandle) => (e: ReactPointerEvent) => {
    if (spaceDown || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { kind: "size", toothId: tooth.id, handle };
    onSelect(tooth.id, null, "focus");
  };

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (panDragRef.current) {
        const d = panDragRef.current;
        setPan({
          x: d.originX + (e.clientX - d.startX),
          y: d.originY + (e.clientY - d.startY),
        });
        return;
      }
      const drag = dragRef.current;
      if (!drag) return;
      const tooth = modelRef.current.teeth.find((t) => t.id === drag.toothId);
      if (!tooth) return;
      const pt = clientToImage(e.clientX, e.clientY);
      const bounds = { width: modelRef.current.width, height: modelRef.current.height };
      if (drag.kind === "shape") {
        onToothChangeRef.current(moveWireToothHandle(tooth, drag.index, pt, bounds));
        return;
      }
      if (drag.kind === "size") {
        onToothChangeRef.current(
          resizeWireToothByHandle(tooth, drag.handle, pt, lockRef.current)
        );
        return;
      }
      onToothChangeRef.current(
        moveWireTooth(tooth, pt.x - drag.lastX, pt.y - drag.lastY, bounds)
      );
      drag.lastX = pt.x;
      drag.lastY = pt.y;
    };
    const onUp = () => {
      dragRef.current = null;
      panDragRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [clientToImage]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-2 px-1 pb-2 shrink-0">
        <p className="text-xs uppercase tracking-wider text-[#2fa9bf] font-semibold">
          Smile zoom
        </p>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => applyFullImageView(true)}
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#2fa9bf]"
          >
            <FiRefreshCw className="w-3 h-3" />
            Fit
          </button>
          <button
            type="button"
            onClick={() => applySmileZoom(true)}
            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-[#2fa9bf]"
          >
            <FiTarget className="w-3 h-3" />
            Smile
          </button>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - 0.25) * 100) / 100))
            }
            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:bg-white/5"
            aria-label="Zoom out"
          >
            <FiMinus className="w-3 h-3" />
          </button>
          <span className="text-xs font-mono text-[#2fa9bf] w-10 text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + 0.25) * 100) / 100))
            }
            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:bg-white/5"
            aria-label="Zoom in"
          >
            <FiPlus className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div
        ref={containerRef}
        className={`relative flex-1 min-h-[280px] rounded-xl overflow-hidden border border-[#2fa9bf]/25 bg-[#101211] touch-none select-none ${
          spaceDown ? "cursor-grab" : ""
        }`}
        onPointerDown={onBackgroundPointerDown}
      >
        <div
          className="absolute pointer-events-none"
          style={{
            left: offsetX,
            top: offsetY,
            width,
            height,
            transform: `scale(${scale})`,
            transformOrigin: "0 0",
          }}
        >
          <canvas
            ref={photoCanvasRef}
            width={width}
            height={height}
            className="absolute inset-0 w-full h-full select-none"
          />
          <svg
            className="absolute inset-0 z-10 overflow-visible"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ pointerEvents: "none" }}
            aria-label="Live tooth editor"
          >
            {shapes.map(({ tooth, index, d }) => {
              const inGroup = groupIds.includes(tooth.id);
              const isPrimary = tooth.id === selectedId;
              return (
                <path
                  key={tooth.id}
                  d={d}
                  fill={
                    isPrimary
                      ? "rgba(47,169,191,0.16)"
                      : inGroup
                        ? "rgba(47,169,191,0.08)"
                        : "rgba(255,255,255,0.001)"
                  }
                  stroke={isPrimary || inGroup ? SELECTED : "rgba(255,255,255,0.35)"}
                  strokeWidth={(isPrimary ? 1.8 : inGroup ? 1.35 : 0.7) * uiScale}
                  strokeDasharray={inGroup && !isPrimary ? `${4 * uiScale} ${2.5 * uiScale}` : undefined}
                  strokeLinejoin="round"
                  style={{
                    pointerEvents: "all",
                    cursor: isPrimary ? "move" : "pointer",
                  }}
                  onPointerDown={onToothPointerDown(tooth)}
                  onDoubleClick={onToothDoubleClick(tooth)}
                >
                  <title>
                    {inGroup
                      ? "Ctrl+click to add or remove · width and height apply to every selected tooth"
                      : `Tooth ${index + 1} — click to edit, Ctrl+click to add`}
                  </title>
                </path>
              );
            })}
            {selected && box && (
              <g>
                <rect
                  x={box.x0}
                  y={box.y0}
                  width={box.w}
                  height={box.h}
                  fill="none"
                  stroke={SIZE_FILL}
                  strokeWidth={0.9 * uiScale}
                  strokeDasharray={`${3.5 * uiScale} ${2.5 * uiScale}`}
                  pointerEvents="none"
                />
                {SIZE_HANDLES.map(({ id, cursor }) => {
                  const p = sizePoint(box, id);
                  const s = (id.length === 2 ? 0.72 : 0.58) * handlePx;
                  return (
                    <rect
                      key={id}
                      x={p.x - s / 2}
                      y={p.y - s / 2}
                      width={s}
                      height={s}
                      rx={0.12 * s}
                      fill={SIZE_FILL}
                      stroke="#1A1714"
                      strokeWidth={0.8 * uiScale}
                      style={{ pointerEvents: "all", cursor }}
                      onPointerDown={onSizeHandleDown(selected, id)}
                    >
                      <title>Drag to change size</title>
                    </rect>
                  );
                })}
              </g>
            )}
            {selected &&
              handlePts.map((p, i) => {
                const active = selectedHandle === i;
                return (
                  <circle
                    key={i}
                    cx={p.x}
                    cy={p.y}
                    r={(active ? 0.68 : 0.55) * handlePx}
                    fill={active ? SELECTED : "#ffffff"}
                    stroke="#1A1714"
                    strokeWidth={0.9 * uiScale}
                    style={{ pointerEvents: "all", cursor: "grab" }}
                    onPointerDown={onShapeHandleDown(selected, i)}
                    onContextMenu={onShapeHandleContext(selected, i)}
                  >
                    <title>Drag to reshape · right-click to remove</title>
                  </circle>
                );
              })}
          </svg>
        </div>
      </div>
    </div>
  );
}
